import { Node, Tree } from '@lukeaus/plain-tree';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import getValueByKey from '../../utils/getValueByKey';
import {
  ENGINE_SITE_NAME,
  MARINE_SITE_NAME,
  PE_SITE_NAME,
  PSP_SITE_NAME,
  urlProductParamNameMap,
} from '../../utils/constants';
import { variant1, variant2, variant3, variant4 } from '../ProductCard/utils';

/**
 * Map engine web catalog to the expected data structure by UI component.
 * @param {Object[]} series - The series with all the crankshafts and models.
 * @returns {Tree} engine catalog data in tree structure
 */
export const getEngineMappedCatalogData = series => {
  if (!Array.isArray(series) || !series?.length) return undefined;
  const rootNode = new Node('root');
  const tree = new Tree(rootNode);
  series?.forEach(item => {
    const seriesNode = rootNode.addChild({
      value: item?.name,
      text: item?.name,
      id: item?.id,
    });

    item?.crankshafts?.forEach(crankshaft =>
      crankshaft?.models?.forEach(model => {
        seriesNode.addChild({
          text: model?.name,
          value: model?.detIdentifier?.value,
          name: model?.name,
          detIdentifier: model?.detIdentifier?.value,
          model: crankshaft?.name,
          modelPage: model?.modelPage?.fields,
          thumbnail: getValueByKey(model, 'images')?.[0],
          id: model?.id,
        });
      }),
    );
  });
  return tree;
};

/**
 * Map marine web catalog to the expected data structure by UI component.
 * @param {Object[]}outboards
 * @returns {Tree} marine catalog data in tree structure
 */
export const getMarineMappedCatalogData = outboards => {
  if (!Array.isArray(outboards) || !outboards?.length) return undefined;
  const rootNode = new Node('root');
  const tree = new Tree(rootNode);
  outboards?.forEach(outboard => {
    const outboardsNode = rootNode.addChild({
      value: outboard?.name,
      text: outboard?.name,
      id: outboard?.id,
    });

    outboard?.models?.forEach(model => {
      const thumbnail = getValueByKey(model, 'primaryImage')?.[0]?.item?.value;
      outboardsNode.addChild({
        text: model?.name,
        value: model?.detIdentifier?.value,
        name: model?.modelName?.value,
        detIdentifier: model?.detIdentifier?.value,
        model: model?.name,
        modelPage: model?.modelPage?.fields,
        thumbnail: {
          ...thumbnail,
          url: thumbnail?.src,
        },
        id: model?.id,
      });
    });
  });
  return tree;
};

/**
 * Map PSP web catalog to the expected data structure by UI component.
 * @param {Object[]} categories
 * @returns { Tree } psp catalog data in tree structure
 */
export const getPspMappedCatalogData = vehicleTypesWithCategories => {
  if (!Array.isArray(vehicleTypesWithCategories) || !vehicleTypesWithCategories?.length) return undefined;
  const rootNode = new Node('root');
  const tree = new Tree(rootNode);

  vehicleTypesWithCategories.forEach(v => {
    const vehicleType = v?.name?.toUpperCase();
    const categories = v?.categories;
    categories?.forEach(category => {
      const categoryNode = rootNode.addChild({ text: category?.name, value: category?.categoryName?.value });
      category?.modelYears?.forEach(modelYear => {
        const yearNode = categoryNode.addChild({ text: modelYear?.year?.value, value: modelYear?.year?.value });
        modelYear?.models?.forEach(model =>
          model?.trims?.forEach(trim => {
            const trimNode = yearNode.addChild({
              text: trim?.name,
              value: trim?.detIdentifier?.value,
              name: trim?.name,
              detIdentifier: trim?.detIdentifier?.value,
              detKey: trim?.detKey?.value,
              modelPage: modelYear?.modelYearPage?.fields,
              thumbnail: trim?.primaryThumbnail?.item?.value,
              id: trim?.sotId?.value,
              modelKey: model?.detKey?.value,
              modelYear: modelYear?.year?.value,
              vehicleType,
            });
            trim?.transmissions?.[0]?.items.forEach(transmission => {
              trimNode.addChild({
                text: transmission?.name,
                value: transmission?.transmissionName?.value,
                modelCode: transmission?.modelCode,
                detIdentifier: transmission?.detIdentifier?.value,
                detKey: transmission?.detKey?.value,
                defaultExteriorColor: transmission?.defaultExteriorColor?.fields,
                defaultExteriorColorKey: transmission?.defaultExteriorColor?.fields?.color?.fields?.detKey?.value,
                hidePriceAndFinance: transmission?.hidePriceAndFinance?.value,
              });
            });
          }),
        );
      });
    });
  });

  return tree;
};

/**
 * Map PE web catalog to the expected data structure by UI component.
 * @param {Object[]}subCategories
 * @returns {Tree} PE catalog data in tree structure
 */
export const getPeMappedCatalogData = subCategories => {
  if (!Array.isArray(subCategories) || !subCategories?.length) return undefined;
  const rootNode = new Node('root');
  const tree = new Tree(rootNode);
  subCategories.forEach(subCategory => {
    const subCategoriesNode = rootNode.addChild({
      value: subCategory?.name,
      text: subCategory?.name,
      id: subCategory?.name,
    });

    subCategory.models?.forEach(model => {
      const thumbnail = model?.primaryImage?.item?.value;
      subCategoriesNode.addChild({
        text: model?.name,
        value: model?.detIdentifier?.value,
        name: model?.modelName?.value,
        detIdentifier: model?.detIdentifier?.value,
        model: model?.name,
        modelCode: model?.modelCode?.value,
        modelPage: subCategory?.subCategoryPage?.fields,
        detKey: model?.detKey?.value,
        detModelKey: model?.detModelKey?.value,
        detTransmissionId: model?.detTransmissionId?.value,
        disableBuyNow: model?.disableBuyNow?.value,
        hasSpecialOffer: model?.hasSpecialOffer?.value,
        thumbnail: {
          ...thumbnail,
          url: thumbnail?.src,
        },
        id: model?.sotId?.value,
      });
    });
  });

  return tree;
};

export const getUrlParamName = appName => urlProductParamNameMap.get(appName) || '';

/**
 * @import {ReferenceCompareProduct} from '../ProductCompare/useSyncUrlAndLocalStorage'}
 * @import {AppName} from '../../appTypes'
 * @param {AppName} appName
 * @param {string} componentName
 * @param {object} fields
 */
export const useAppProps = (appName, componentName, fields) => {
  const { t } = useTranslation();

  const gtmTags = {
    'data-gtm-component-type': componentName,
  };

  return useMemo(() => {
    switch (appName) {
      case ENGINE_SITE_NAME:
        return {
          goBackButton: t('Shared.Common.goBackButton'),
          viewDetailsButton: t('Shared.Common.viewDetailsButton'),
          sectionHeadingTitle: t('Shared.Common.compareSectionHeading'),
          selectProductsToCompare: t('Shared.Common.selectProductsToCompareLabel'),
          productCatalogDataTree: getEngineMappedCatalogData(fields?.data?.item?.series),
          legalDisclaimer: '',
          variant: variant1,
          /** @type {ReferenceCompareProduct[]} */
          referenceCompareProducts:
            fields?.data?.item?.series
              ?.reduce((all, serie) => [...all, ...(serie?.crankshafts ?? [])], [])
              ?.reduce((all, crankshaft) => [...all, ...(crankshaft?.models ?? [])], [])
              ?.map(model => ({
                detId: model?.detIdentifier?.value ?? '',
                category: {
                  key: 'categoryName',
                  value: false,
                },
              })) ?? [],
        };

      case MARINE_SITE_NAME:
        return {
          sectionHeadingTitle: t('Shared.Common.compareSectionHeading'),
          goBackButton: t('Shared.Common.goBackButton'),
          viewDetailsButton: t('Shared.Common.viewDetailsButton'),
          selectProductsToCompare: t('Shared.Common.selectProductsToCompareLabel'),
          productCatalogDataTree: getMarineMappedCatalogData(fields?.data?.item?.outboards),
          legalDisclaimer: '',
          gtmTags: Object.freeze({
            ...gtmTags,
            'data-gtm-interaction-type': 'cta: explore',
            'data-gtm-title': 'view details',
          }),
          variant: variant2,
          /** @type {ReferenceCompareProduct[]} */
          referenceCompareProducts:
            fields?.data?.item?.outboards
              ?.reduce((all, outboard) => [...all, ...(outboard?.models ?? [])], [])
              ?.map(model => ({
                detId: model?.detIdentifier?.value ?? '',
                category: {
                  key: 'categoryName',
                  value: false,
                },
              })) ?? [],
        };

      case PSP_SITE_NAME:
        return {
          productLine: fields?.data?.value?.productLine?.fields?.name,
          legalDisclaimer: fields?.data?.value?.legalDisclaimer?.value,
          sectionHeadingTitle: fields?.data?.value?.compareTrimsTitle?.value,
          goBackButton: t('Shared.CompareTrims.goBackButton'),
          viewDetailsButton: t('Shared.CompareTrims.viewModelDetailsButton'),
          selectProductsToCompare: t('Shared.CompareTrims.selectProductsToCompareLabel'),
          productCatalogDataTree: getPspMappedCatalogData(fields?.data?.value?.productLine?.fields?.vehicleTypes),
          gtmTags: Object.freeze({
            ...gtmTags,
            'data-gtm-interaction-type': 'cta: explore',
            'data-gtm-title': 'view details',
          }),
          paymentOptions: {
            paymentMethod: 'finance',
            paymentFrequency: 'weekly',
          },
          variant: variant3,
          /** @type {ReferenceCompareProduct[]} */
          referenceCompareProducts:
            fields?.data?.value?.productLine?.fields?.vehicleTypes?.reduce((allTypes, vehicleType) => {
              const vehicleTypeName = vehicleType?.name ?? '';
              return [
                ...allTypes,
                ...((vehicleType?.categories ?? [])
                  ?.reduce((all, category) => [...all, ...(category?.modelYears ?? [])], [])
                  ?.reduce((all, modelYear) => [...all, ...(modelYear?.models ?? [])], [])
                  ?.reduce((all, model) => [...all, ...(model?.trims ?? [])], [])
                  ?.reduce((all, trim) => [...all, ...(trim?.transmissions ?? [])], [])
                  ?.reduce((all, transmission) => [...all, ...(transmission?.items ?? [])], [])
                  ?.map(item => ({
                    detId: item?.detIdentifier?.value ?? '',
                    category: {
                      key: 'vehicleType',
                      value: vehicleTypeName,
                    },
                  })) ?? []),
              ];
            }, []) ?? [],
        };

      case PE_SITE_NAME:
        return {
          sectionHeadingTitle: fields?.data?.value?.compareTitle?.value,
          goBackButton: t('Shared.Common.goBackButton'),
          viewDetailsButton: t('Shared.Common.viewDetailsButton'),
          selectProductsToCompare: t('Shared.CompareTrims.selectProductsToCompareLabel'),
          productCatalogDataTree: getPeMappedCatalogData(fields?.data?.value?.category?.fields?.subCategories),
          legalDisclaimer: '',
          isDownloadCtaValid: false,
          sellingPriceTooltipDiscalimerLabel: t('Shared.Common.sellingPriceTooltipText'),
          variant: variant4,
          /** @type {ReferenceCompareProduct[]} */
          referenceCompareProducts:
            fields?.data?.value?.category?.fields?.subCategories
              ?.reduce((all, subCategory) => [...all, ...(subCategory?.models ?? [])], [])
              ?.map(model => ({
                detId: model?.detIdentifier?.value ?? '',
                category: {
                  key: 'categoryName',
                  value: fields?.data?.value?.category?.fields?.name ?? '',
                },
              })) ?? [],
          gtmTags: Object.freeze({
            ...gtmTags,
            'data-gtm-body-style': fields?.data?.value?.gtmBodyStyle?.value.toLowerCase(),
            'data-gtm-category': fields?.data?.value?.category?.fields?.name.toLowerCase(),
            'data-gtm-interaction-type': 'cta: explore',
            'data-gtm-title': 'view details',
          }),
        };

      default:
        return null;
    }
  }, [appName, componentName, fields]);
};
